import { timeAsync } from '@ifixit/helpers';
import { type ScopeContext, SentryError } from '@ifixit/sentry';
import { DEFAULT_RETRY_OPTIONS, robustFetch } from '@ifixit/utils';

// Extend Window interface to include IFixitAPIClient
declare global {
   interface Window {
      IFixitAPIClient: typeof IFixitAPIClient;
   }
}

export * from './client';

export const iFixitAPIRequestHeaderName = 'X-iFixit-API-Request';

export interface ClientOptions {
   origin: string;
   version?: string;
   headers?: HeadersInit;
   disableCacheGets?: boolean;
}

const BypassHeaderName = 'Nextjs-Bypass-Varnish';
export const VarnishBypassHeader = { [BypassHeaderName]: '1' };

export const iFixitAPIRequestHeader = { [iFixitAPIRequestHeaderName]: '1' };

const DEFAULT_RETRY_STATUSES = DEFAULT_RETRY_OPTIONS.retryableStatuses;

export interface UploadAssetResult {
   assetId: number;
   name: string;
   mediaType: 'image' | 'document' | 'video';
}

export interface UploadAssetOptions {
   onProgress?: (progress: number) => void;
   signal?: AbortSignal;
}

export class UploadAbortError extends Error {
   constructor() {
      super('Upload aborted');
      this.name = 'UploadAbortError';
   }
}

export class IFixitAPIClient {
   static activeRequests: Map<
      IFixitAPIClient,
      {
         url: string;
         method: string;
         headers: Headers;
         endpoint: string;
      }
   > = new Map();
   static completedRequests = 0;

   origin: string;
   version: string;

   headers: HeadersInit;
   disableCacheGets: boolean;

   constructor(readonly options: ClientOptions) {
      this.origin = options.origin;
      this.version = options.version ?? '2.0';
      this.headers = options.headers ?? {};
      this.disableCacheGets = options.disableCacheGets ?? false;
   }

   async getRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'GET',
      });
   }

   async get<Data = unknown>(
      endpoint: string,
      { statName }: { statName: string },
      init?: RequestInit
   ): Promise<Data> {
      const url = new URL(endpoint, this.origin);
      if (this.disableCacheGets) {
         url.searchParams.set('disableCacheGets', '1');
      }
      const newEndpoint = `${url.pathname}${url.search}`;
      return this.getRaw(newEndpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: newEndpoint, init })
      );
   }

   async postRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'POST',
      });
   }

   async post<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.postRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async putRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'PUT',
      });
   }

   async put<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.putRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async patchRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'PATCH',
      });
   }

   async patch<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.patchRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   async deleteRaw(endpoint: string, statName: string, init?: RequestInit): Promise<Response> {
      return this.fetch(endpoint, statName, {
         ...init,
         method: 'DELETE',
      });
   }

   async delete<Data = unknown>(
      endpoint: string,
      statName: string,
      init?: RequestInit
   ): Promise<Data> {
      return this.deleteRaw(endpoint, statName, init).then(resp =>
         this.getJsonFromResponse(resp, { input: endpoint, init })
      );
   }

   // Browser-only: posts a File to /user/media/upload via XMLHttpRequest so
   // upload progress can be reported. fetch() can't surface upload progress
   // events cross-browser, so we mirror this.fetch's auth/header conventions
   // by hand here.
   uploadAsset(file: File, options: UploadAssetOptions = {}): Promise<UploadAssetResult> {
      const { onProgress, signal } = options;
      return new Promise((resolve, reject) => {
         if (signal?.aborted) {
            reject(new UploadAbortError());
            return;
         }
         const url = `${this.origin}/api/${this.version}/user/media/upload?file=${encodeURIComponent(file.name)}`;
         const xhr = new XMLHttpRequest();
         xhr.open('POST', url);
         xhr.withCredentials = true;

         const pskToken = process.env.NEXT_PUBLIC_DEV_API_AUTH_TOKEN;
         const headers = new Headers({
            ...(pskToken ? { Authorization: `PSK ${pskToken}` } : {}),
            ...this.headers,
            ...iFixitAPIRequestHeader,
            'Content-Type': file.type || 'application/octet-stream',
         });
         headers.forEach((value, key) => xhr.setRequestHeader(key, value));

         xhr.upload.onprogress = e => {
            if (e.lengthComputable && onProgress) {
               onProgress(Math.round((e.loaded / e.total) * 100));
            }
         };

         xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
               try {
                  const data = JSON.parse(xhr.responseText) as {
                     id: number;
                     name: string;
                     media_type: string;
                  };
                  resolve({
                     assetId: data.id,
                     name: data.name,
                     mediaType: normalizeMediaType(data.media_type),
                  });
               } catch {
                  reject(new SentryError('Could not parse upload response'));
               }
            } else {
               reject(new SentryError(`Upload failed (${xhr.status})`));
            }
         };
         xhr.onerror = () => reject(new SentryError('Upload failed'));
         xhr.onabort = () => reject(new UploadAbortError());

         const onAbort = () => xhr.abort();
         signal?.addEventListener('abort', onAbort, { once: true });
         xhr.onloadend = () => signal?.removeEventListener('abort', onAbort);

         xhr.send(file);
      });
   }

   async fetch(endpoint: string, statName: string, init?: RequestInit) {
      const pskToken = process.env.NEXT_PUBLIC_DEV_API_AUTH_TOKEN;
      const authHeader: { Authorization?: string } = pskToken
         ? { Authorization: `PSK ${pskToken}` }
         : {};
      const slashEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
      const url = `${this.origin}/api/${this.version}${slashEndpoint}`;
      const headers = new Headers({
         'User-Agent': 'iFixitNextBackendNinja/1.0',
         ...authHeader,
         ...this.headers,
         ...init?.headers,
         ...iFixitAPIRequestHeader,
      });

      const requestInfo = {
         url,
         method: init?.method || 'GET',
         headers: headers,
         endpoint,
      };

      // Track active request
      IFixitAPIClient.activeRequests.set(this, requestInfo);

      try {
         const method = init?.method?.toUpperCase() ?? 'GET';
         const isIdempotent = ['GET', 'HEAD', 'OPTIONS'].includes(method);

         const response = await timeAsync(`ifixit-api.${method.toLowerCase()}.${statName}`, () =>
            robustFetch(url, {
               credentials: 'include',
               ...init,
               headers,
               retry: {
                  ...(!isIdempotent && { maxRetries: 0 }),
                  retryableStatuses: DEFAULT_RETRY_STATUSES.filter(s => ![503, 504].includes(s)),
               },
            })
         );

         // Complete request tracking
         IFixitAPIClient.completedRequests++;

         warnIfNotBypassed(headers, response);
         return response;
      } finally {
         // Handle request failure
         IFixitAPIClient.activeRequests.delete(this);
      }
   }

   isJson = (response: Response) => response.headers.get('Content-Type') === 'application/json';

   getJsonFromResponse = async (
      response: Response,
      request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) => {
      if (!response.ok) {
         await this.throwFetchError(response, request);
      }

      if (this.isJson(response)) {
         return response.json();
      }

      return null;
   };

   throwFetchError = async (
      response: Response,
      request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) => {
      const isJson = this.isJson(response);

      const message = response.statusText;

      let rawText: string | null = null;
      try {
         rawText = await response.text();
      } catch {
         // Body unreadable; leave rawText null.
      }

      let body: unknown = rawText;
      if (rawText !== null && isJson) {
         try {
            body = JSON.parse(rawText);
         } catch {
            body = rawText;
         }
      }
      const body_key = isJson ? 'response_json' : 'response_text';

      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
         headers[key] = value;
      });

      const body_size = rawText === null ? 0 : new TextEncoder().encode(rawText).length;

      const sentryDetails: Partial<ScopeContext> = {
         contexts: {
            request,
            response: {
               type: 'response',
               status_code: response.status,
               headers,
               body_size,
               [body_key]: body,
            },
         },
         tags: {
            request_url: response.url,
            response_status_code: response.status.toString(),
            response_status_text: response.statusText,
         },
      };

      throw new FetchError(message, sentryDetails, response, request);
   };
}

// Make IFixitAPIClient available globally for testing purposes
if (typeof window !== 'undefined') {
   window.IFixitAPIClient = IFixitAPIClient;
}

export class FetchError extends SentryError {
   constructor(
      message: string,
      readonly sentryDetails: Partial<ScopeContext>,
      readonly response: Response,
      readonly request?: {
         input: RequestInfo | URL;
         init?: RequestInit;
      }
   ) {
      super(message, sentryDetails);
   }
}

function warnIfNotBypassed(requestHeaders: Headers, response: Response): void {
   const wantsBypass = requestHeaders.has(BypassHeaderName);
   if (!wantsBypass) {
      return;
   }

   const cachedHeaderKey = 'x-debug-cache';

   if (!response.headers.has(cachedHeaderKey)) {
      console.warn(
         'Varnish bypass requested, expected header not found!',
         `Expected header: ${cachedHeaderKey}`
      );
      return;
   }

   const cached = response.headers.get(cachedHeaderKey);
   const responseBypassed = cached === 'MISS';
   if (responseBypassed) {
      return;
   }

   console.warn(
      `Varnish bypass requested, but didn't bypass for ${truncate(response.url, 100)}!`,
      `${cachedHeaderKey}: ${cached}`
   );
}

function truncate(str: string, length: number) {
   return str.length < length ? str : str.slice(0, length - 3) + '...';
}

function normalizeMediaType(raw: string): UploadAssetResult['mediaType'] {
   // eslint-disable-next-line unicorn/prefer-includes-over-repeated-comparisons -- the equality chain narrows raw to the literal union so it can be returned
   if (raw === 'image' || raw === 'document' || raw === 'video') return raw;
   return 'document';
}
